import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNetworkStatus } from '../utils/useNetworkStatus';
import { WifiIcon, WifiOffIcon, XIcon } from './Icons';

export const NetworkStatusToast: React.FC = () => {
  const isOnline = useNetworkStatus();
  const [toastType, setToastType] = useState<'online' | 'offline'>(() =>
    isOnline ? 'online' : 'offline',
  );
  const [show, setShow] = useState(() => !isOnline);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    Promise.resolve().then(() => {
      if (!isOnline) {
        setToastType('offline');
        setShow(true);
      } else {
        setToastType('online');
        setShow(true);
      }
    });

    if (isOnline) {
      // オンラインに復帰した場合は3秒後に自動で閉じる
      const timer = setTimeout(() => {
        setShow(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  return (
    <AnimatePresence>
      {show && (
        <div
          className="fixed inset-x-4 z-40 flex justify-center pointer-events-none"
          style={{ top: 'calc(var(--safe-area-top, var(--app-safe-top, 59px)) + 3.5rem)' }}
        >
          <motion.div
            initial={{ opacity: 0, y: -40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className={`pointer-events-auto flex items-center space-x-3 px-4 py-3 rounded-full shadow-[0_8px_30px_rgba(0,0,0,0.2)] border backdrop-blur-md max-w-sm w-auto ${
              toastType === 'offline'
                ? 'bg-red-950/80 border-red-800/30 text-red-100'
                : 'bg-emerald-950/80 border-emerald-800/30 text-emerald-100'
            }`}
          >
            {/* アイコン */}
            <div className="flex-shrink-0">
              {toastType === 'offline' ? (
                <WifiOffIcon size={16} className="text-red-400 animate-pulse" />
              ) : (
                <WifiIcon size={16} className="text-emerald-400" />
              )}
            </div>

            {/* メッセージ */}
            <span className="text-[12px] font-medium tracking-tight select-none">
              {toastType === 'offline'
                ? 'オフラインです。保存済みの小説が閲覧可能です。'
                : 'オンラインに復帰しました。'}
            </span>

            {/* 手動で閉じるボタン */}
            <button
              type="button"
              onClick={() => setShow(false)}
              aria-label="閉じる"
              className={`p-0.5 rounded-full transition-colors ${
                toastType === 'offline'
                  ? 'hover:bg-red-900/40 text-red-300 hover:text-red-100'
                  : 'hover:bg-emerald-900/40 text-emerald-300 hover:text-emerald-100'
              }`}
            >
              <XIcon size={12} />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
